import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Box,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo, useRef, useState } from "react";
import { DelayByShift } from "../models/DelayByShift";

const DURATION_UNIT = {
	Minutes: "minutes",
	Hours: "hours",
} as const;

type DurationUnit = (typeof DURATION_UNIT)[keyof typeof DURATION_UNIT];

const DURATION_UNIT_CONFIG = {
	[DURATION_UNIT.Minutes]: {
		label: "Minutes",
		abbreviation: "min",
		precision: 1,
	},
	[DURATION_UNIT.Hours]: {
		label: "Hours",
		abbreviation: "hrs",
		precision: 2,
	},
} as const;

const DAILY_TREND_CONTENT_HEIGHT = "max(400px, calc(100vh - 380px))";

export const DelaysDailyTrend = ({
	delays,
	sectionName,
}: {
	delays: DelayByShift[];
	sectionName?: string | null;
}) => {
	const chartRef = useRef<ReactECharts>(null);
	const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
	const [durationUnit, setDurationUnit] = useState<DurationUnit>(
		DURATION_UNIT.Minutes,
	);

	const dailyData = useMemo(() => {
		const dateMap = new Map<string, { duration: number; count: number }>();

		for (const delay of delays) {
			const dateKey = dxtToLocalServerTime(delay.Start!, "yyyy-MM-dd");
			const existing = dateMap.get(dateKey);
			if (existing) {
				existing.duration += delay.Duration || 0;
				existing.count += 1;
			} else {
				dateMap.set(dateKey, { duration: delay.Duration || 0, count: 1 });
			}
		}

		return Array.from(dateMap.entries())
			.map(([dateStr, data]) => ({
				date: dateStr,
				durationSec: data.duration,
				durationMin: data.duration / 60,
				durationHrs: data.duration / 3600,
				eventCount: data.count,
			}))
			.sort((a, b) => a.date.localeCompare(b.date));
	}, [delays]);

	const handleChartEvents = {
		mouseover: (params: { dataIndex: number }) =>
			setHighlightedIndex(params.dataIndex),
		mouseout: () => setHighlightedIndex(null),
	};

	const handleTableRowHover = (index: number | null) => {
		setHighlightedIndex(index);
		const chartInstance = chartRef.current?.getEchartsInstance();
		if (chartInstance) {
			if (index !== null) {
				chartInstance.dispatchAction({
					type: "highlight",
					seriesIndex: 0,
					dataIndex: index,
				});
				chartInstance.dispatchAction({
					type: "showTip",
					seriesIndex: 0,
					dataIndex: index,
				});
			} else {
				chartInstance.dispatchAction({ type: "downplay", seriesIndex: 0 });
				chartInstance.dispatchAction({ type: "hideTip" });
			}
		}
	};

	const durationUnitConfig = DURATION_UNIT_CONFIG[durationUnit];
	const chartOption = {
		title: {
			text: `Daily Delay Duration (${durationUnit})`,
			left: "center",
			textStyle: { fontSize: 13, fontWeight: "bold" },
		},
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			formatter: (
				params: { name: string; value: number; dataIndex: number }[],
			) => {
				const p = params[0];
				const duration = p.value.toFixed(durationUnitConfig.precision);
				return `${p.name}<br/>Duration: ${duration} ${durationUnitConfig.abbreviation}<br/>Events: ${dailyData[p.dataIndex].eventCount}`;
			},
		},
		grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
		xAxis: {
			type: "category",
			data: dailyData.map((d) => d.date.split("-").slice(1).join("/")),
			name: "Date",
			nameLocation: "middle",
			nameGap: 35,
		},
		yAxis: {
			type: "value",
			name: durationUnitConfig.label,
			nameLocation: "middle",
			nameGap: 40,
		},
		series: [
			{
				name: `Duration (${durationUnitConfig.abbreviation})`,
				type: "bar",
				data: dailyData.map((day) => {
					const duration =
						durationUnit === DURATION_UNIT.Minutes
							? day.durationMin
							: day.durationHrs;

					return Number(duration.toFixed(durationUnitConfig.precision));
				}),
				itemStyle: { color: "#e67e22" },
				barWidth: "60%",
				emphasis: { itemStyle: { color: "#d35400" } },
			},
		],
	};

	return (
		<Paper variant="outlined" sx={{ mb: 1 }}>
			<Box
				sx={{
					px: 2,
					py: 1,
					backgroundColor: (theme) => theme.palette.primary.main,
				}}
			>
				<Typography variant="subtitle2" sx={{ color: "white" }}>
					{sectionName ? `${sectionName} — ` : ""}Daily Delay Trend
				</Typography>
			</Box>

			<Divider />

			<Box sx={{ display: "flex", height: DAILY_TREND_CONTENT_HEIGHT }}>
				<Box
					sx={{
						flex: 4,
						borderRight: "1px solid",
						borderColor: "divider",
						overflowX: "hidden",
						overflowY: "auto",
					}}
				>
					<Table size="small">
						<TableHead
							sx={{
								position: "sticky",
								top: 0,
								zIndex: 1,
								backgroundColor: "background.paper",
							}}
						>
							<TableRow>
								<TableCell rowSpan={2}>Date</TableCell>
								<TableCell
									colSpan={3}
									align="center"
									sx={{ borderBottom: "none" }}
								>
									Duration
								</TableCell>
								<TableCell rowSpan={2} align="right">
									# Events
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									seconds
								</TableCell>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									minutes
								</TableCell>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									hours
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{dailyData.map((row, index) => (
								<TableRow
									key={row.date}
									hover
									onMouseEnter={() => handleTableRowHover(index)}
									onMouseLeave={() => handleTableRowHover(null)}
									sx={{
										cursor: "pointer",
										...(highlightedIndex === index
											? { backgroundColor: "action.selected" }
											: {}),
									}}
								>
									<TableCell>
										{row.date.split("-").slice(1).join("/")}
									</TableCell>
									<TableCell align="right">
										{row.durationSec.toFixed(0)}
									</TableCell>
									<TableCell align="right">
										{row.durationMin.toFixed(1)}
									</TableCell>
									<TableCell align="right">
										{row.durationHrs.toFixed(2)}
									</TableCell>
									<TableCell align="right">{row.eventCount}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>

				<Box sx={{ flex: 6, height: "100%", p: 1, position: "relative" }}>
					<ToggleButtonGroup
						value={durationUnit}
						exclusive
						onChange={(_, value: DurationUnit | null) =>
							value && setDurationUnit(value)
						}
						size="small"
						aria-label="Delay duration unit"
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
							zIndex: 1,
							backgroundColor: "background.paper",
							"& .MuiToggleButton-root": {
								px: 1.25,
								py: 0.25,
								fontSize: "0.75rem",
								textTransform: "none",
							},
						}}
					>
						<ToggleButton
							value={DURATION_UNIT.Minutes}
							aria-label="Show duration in minutes"
						>
							Minutes
						</ToggleButton>
						<ToggleButton
							value={DURATION_UNIT.Hours}
							aria-label="Show duration in hours"
						>
							Hours
						</ToggleButton>
					</ToggleButtonGroup>
					<ReactECharts
						ref={chartRef}
						option={chartOption}
						onEvents={handleChartEvents}
						style={{ height: "100%" }}
					/>
				</Box>
			</Box>
		</Paper>
	);
};
